import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { Users, Search, Loader2, CreditCard, Package, ShoppingCart, FileText, Eye, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminCreators() {
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(null);

  const loadCreators = useCallback(async () => {
    try {
      const res = await api.get('/admin/creators', { params: { limit: 100 } });
      setCreators(res.data.creators);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to load creators');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCreators(); }, [loadCreators]);

  const handleDelete = async (e, creator) => {
    e.stopPropagation();
    if (creator.is_admin) { toast.error('Cannot delete admin account'); return; }
    const confirmed = window.confirm(`Delete "${creator.name}" (${creator.email}) and ALL their data (products, orders, pages)?\n\nThis cannot be undone.`);
    if (!confirmed) return;
    setDeleting(creator.id);
    try {
      await api.delete(`/admin/creators/${creator.id}`);
      setCreators(prev => prev.filter(c => c.id !== creator.id));
      setTotal(prev => prev - 1);
      toast.success(`"${creator.name}" deleted`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally { setDeleting(null); }
  };

  const filtered = creators.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.handle?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 max-w-[1200px]" data-testid="admin-creators">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Creators</h1>
          <p className="text-sm text-slate-500 mt-1">{total} registered creator{total !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input className="pl-9 bg-white border-slate-200" placeholder="Search by name, email, or handle..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <Card className="border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Creator</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Handle</TableHead>
              <TableHead className="text-center">Products</TableHead>
              <TableHead className="text-center">Pages</TableHead>
              <TableHead className="text-center">Orders</TableHead>
              <TableHead className="text-right">Revenue</TableHead>
              <TableHead className="text-center">Gateway</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(c => (
              <TableRow key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/admin/creators/${c.id}`)}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      {c.avatar_url ? <AvatarImage src={c.avatar_url} /> : null}
                      <AvatarFallback className="bg-slate-200 text-slate-600 text-xs">{c.name?.[0]?.toUpperCase() || 'C'}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-sm">{c.name}</p>
                      <p className="text-xs text-slate-400">{c.email}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className="text-[10px] text-white capitalize" style={{ backgroundColor: c.plan === 'premium' ? '#FF9A00' : c.plan === 'pro' ? '#195259' : '#6D6E71' }}>
                    {c.plan || 'free'}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-xs text-slate-500">@{c.handle}</TableCell>
                <TableCell className="text-center">{c.products}</TableCell>
                <TableCell className="text-center">{c.landing_pages}</TableCell>
                <TableCell className="text-center">{c.orders}</TableCell>
                <TableCell className="text-right font-semibold">RM {(c.revenue || 0).toFixed(2)}</TableCell>
                <TableCell className="text-center">
                  {c.has_payment_gateway ? (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Active</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-[10px]">None</Badge>
                  )}
                </TableCell>
                <TableCell className="text-xs text-slate-400">
                  {c.created_at ? new Date(c.created_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); navigate(`/admin/creators/${c.id}`); }}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => handleDelete(e, c)} disabled={deleting === c.id || c.is_admin}
                      data-testid={`delete-creator-${c.id}`}>
                      {deleting === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-12 text-slate-400">
                  {search ? 'No creators match your search' : 'No creators registered yet'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
